<template>
    <span
        v-resize="checkRatioSimilarity"
        class="photo-wrapper"
        :class="{
            backgroundless: noBackground,
            reposition: reposition || imageOffset,
            repositioning: isRepositioning,
            fit: isContained,
            fill: cover,
            editable,
            extend
        }"
    >
        <span
            v-if="isContained || cover"
            class="photo-container"
            :class="[imageFit, overlayBorderColour]"
            :style="{ height: hasContainerRatio ? `${component.width / containerRatio}px` : null }"
        >
            <span
                v-if="!cover && !noBackground"
                class="photo-background"
                :class="{ blur }"
                :style="{ backgroundImage: blur ? `url(${url(src)})` : null }"
            />
            <Drag
                v-if="editable && reposition"
                ref="drag"
                :boundary="$el"
                :disabled="!isRepositioning"
                :reverse-boundary="!!cover"
                :shadow="false"
                :reset="false"
                prevent-keyboard
                @position="setImagePosition"
            >
                <img
                    :src="url(src)"
                    :alt="alt || ''"
                    class="photo-image"
                    :style="{ transform: previousImagePosition ? null : transform }"
                    :class="[imageClass, {
                        cover: areRatiosSimilar || cover,
                        square: naturalWidth === naturalHeight,
                        rounded: isContained ? containRounded : rounded
                    }]"
                    @load="onImageLoad"
                />
            </Drag>
            <img
                v-else
                :src="url(src)"
                :alt="alt || ''"
                class="photo-image"
                :style="{ transform }"
                :class="[imageClass, {
                    cover: areRatiosSimilar || cover,
                    square: naturalWidth === naturalHeight,
                    rounded: isContained ? containRounded : rounded,
                    fade,
                    loaded: isLoaded
                }]"
                @load="onImageLoad"
            />
            <Clickable
                v-if="_lightbox"
                :style="clickHandlerStyle"
                class="photo-click-handler"
                aria-label="Open lightbox"
                @click="showLightbox"
            />
            <MediaControls
                v-if="editable && !controlsHidden && (controls || isRepositioning)"
                :controls="_controls"
                :position="isRepositioning ? repositionControlsPosition : controlsPosition"
                class="photo-media-controls"
                :overlay-transparent="overlayTransparent && !cover"
                :border-colour="overlayBorderColour"
                @control="onImageControl"
            />
        </span>
        <template v-else>
            <img
                :src="url(src)"
                :alt="alt || ''"
                :class="[imageClass, { lightbox: _lightbox, fade, loaded: isLoaded }]"
                :style="{ transform }"
                @load="onImageLoad"
                @click="showLightbox"
            />
            <MediaControls
                v-if="editable && controls && !controlsHidden"
                :controls="controls"
                :position="controlsPosition"
                class="photo-media-controls"
                @control="onImageControl"
            />
        </template>
        <slot />
        <div v-if="isRepositioning" class="reposition-hint white">
            <div class="hint">
                <Icon name="reposition" right class="white" />
                <Txt size="xxs" heading>
                    {{ $trans('account.theme.banner.customise.reposition_image') }}
                </Txt>
            </div>
        </div>
        <Lightbox
            v-if="isLightboxShowing"
            :src="url(src)"
            :alt="alt"
            :dimensions="{ naturalHeight, naturalWidth }"
            @close="isLightboxShowing = false"
        />
    </span>
</template>

<script>
import { url } from '@/helpers/utilities';
import { useResponsive, getElement } from '@/store/responsive';
import resize from '@/directives/resize';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Drag from '@/components/interactive/drag';
import Lightbox from '@/components/modals/lightbox';
import Clickable from '@/components/utility/clickable';
import MediaControls from '@/components/media/media-controls';

const DEFAULT_IMAGE_RATIO_BREAKPOINT = { x: 24, y: 24 };
const DEFAULT_CONTAINER_RATIO = 16 / 9;

export default {
    name: 'Photo',
    components: { Txt, Icon, Drag, Lightbox, Clickable, MediaControls },
    directives: { resize },
    props: {
        src: { type: String, default: '' },
        alt: { type: String, default: '' },
        cover: { type: Boolean, default: false },
        // Passing `contain` will make the image become cover instead of contain within so many
        // pixels of having the same ratio as `ratio` for each axis. You can pass a number to
        // set the breakpoint on both axes or you can pass an object to specify different
        // breakpoints for each axis. Otherwise just passing `contain` will use the default
        // image ratio breakpoint.
        contain: { type: [Boolean, Object, Number], default: false },
        extend: { type: Boolean, default: false },
        // If `ratio` is passed as a boolean then the default container ratio will be used,
        // otherwise you can pass a number to set the ratio. If `contain` isn't set or ratio
        // isn't passed then a ratio will not be used.
        ratio: { type: [Number, Boolean], default: undefined },
        // Only applies to a contained image's background.
        blur: { type: Boolean, default: true },
        // Allow for cases where the image should't have border-radius
        rounded: { type: Boolean, default: true },
        // Allow rounded corners on contain mode
        containRounded: { type: Boolean, default: false },
        // Allow for no background when image is in fit mode
        noBackground: { type: Boolean, default: false },
        // Pass imageRatio when you know the image's dimensions ahead of time.
        imageRatio: { type: Number, default: undefined },
        // Image class will always apply to the img element itself. Which may or may not be the
        // root of the component.
        imageClass: { type: String, default: '' },
        lightbox: { type: Boolean, default: false },
        // Displays a media editor on hover that will emit events
        editable: { type: Boolean, default: false },
        // An array of controls, see the <MediaControls> component for more info
        controls: { type: Array, default: undefined },
        // Where should the controls be displayed, see the <MediaControls> component for more info
        controlsPosition: { type: String, default: 'bottom-center' },
        repositionControlsPosition: { type: String, default: 'top-right' },
        // Whether or not the controls are visible (only applicable if editable is true)
        controlsHidden: { type: Boolean, default: false },
        // For media controls overlay styling - only for when image is in fit mode and when background should be transparent
        overlayTransparent: { type: Boolean, default: false },
        overlayBorderColour: { type: String, default: null },
        reposition: { type: Boolean, default: false },
        imageOffset: { type: [Object, Boolean], default: undefined },
        fade: { type: Boolean, default: false }
    },
    emits: ['control', 'resize', 'reposition', 'repositioning', 'loaded', 'image:height', 'update:offset'],
    data() {
        return {
            image: null,
            areRatiosSimilar: false,

            currentImagePosition: this.imageOffset || { x: 0, y: 0 },
            previousImagePosition: null,
            
            isLoaded: false,
            isRepositioning: false,
            isLightboxShowing: false,
            viewport: useResponsive()
        };
    },
    computed: {
        component() {
            return getElement(this);
        },
        hasReposition() {
            return this.controls.some(control => control.type === 'reposition');
        },
        imageFit() {
            if (this.isContained) {
                return this.calculatedImageRatio > this.calculatedContainerRatio ? 'fit-width' : 'fit-height';
            } else {
                return this.calculatedContainerRatio > this.calculatedImageRatio ? 'fit-width' : 'fit-height';
            }
        },
        ratioType() {
            return typeof this.ratio;
        },
        isContained() {
            return Boolean(this.contain);
        },
        hasContainerRatio() {
            return this.ratioType !== 'undefined' && (this.isContained || this.cover);
        },
        containerRatio() {
            return {
                undefined: DEFAULT_CONTAINER_RATIO,
                boolean: DEFAULT_CONTAINER_RATIO,
                number: this.ratio
            }[this.ratioType];
        },
        naturalHeight() {
            return this.image && this.image.naturalHeight;
        },
        naturalWidth() {
            return this.image && this.image.naturalWidth;
        },
        clientHeight() {
            return this.image && this.image.clientHeight;
        },
        calculatedContainerRatio() {
            if (!this.component) return null;
            return this.component.width / this.component.height;
        },
        calculatedImageRatio() {
            if (this.imageRatio) return this.imageRatio;
            if (!this.image) return null;
            return this.naturalWidth / this.naturalHeight;
        },
        imageRatioBreakpoint() {
            const containValue = this.contain;
            return {
                boolean: DEFAULT_IMAGE_RATIO_BREAKPOINT,
                object: containValue,
                number: { x: containValue, y: containValue }
            }[typeof containValue];
        },
        clickHandlerStyle() {
            if (this.cover) return { top: 0, left: 0, right: 0, bottom: 0 };
            const containerWidth = this.component.width;
            const containerHeight = this.component.height;
            let width = 0;
            let height = 0;
            if (this.isContained ? (this.calculatedImageRatio > this.calculatedContainerRatio) : (this.calculatedImageRatio < this.calculatedContainerRatio)) {
                width = containerWidth;
                height = width / this.calculatedImageRatio;
            } else {
                height = containerHeight;
                width = height * this.calculatedImageRatio;
            }
            return {
                width: `${width}px`,
                height: `${height}px`,
                left: `${(containerWidth - width) / 2}px`,
                top: `${(containerHeight - height) / 2}px`
            };
        },
        transform() {
            if (!this.imageOffset) return;
            return `translate(${this.imageOffset.x}%, ${this.imageOffset.y}%)`;
        },
        _controls() {
            if (this.isRepositioning) return [{ type: 'confirm:reposition' }, { type: 'cancel:reposition' }];
            const controls = this.controls ? [ ...this.controls ] : [];
            if (this.reposition && !this.hasReposition) {
                controls.push({ type: 'divider' });
                controls.push({ type: 'reposition', isActive: this.isRepositioning, tooltip: this.$trans('image.reposition') });
            }
            return controls;
        },
        _lightbox() {
            if (this.$access.isAuthor && this.viewport.is.mobile) return false;
            return this.lightbox && !this.editable;
        }
    },
    watch: {
        component: {
            handler(to) {
                if (!to) return;
                this.$emit('resize', { width: to.width, height: to.height });
            },
            deep: true,
            immediate: true
        },
        clientHeight(to) {
            if (to) this.$emit('image:height', to);
        },
        isRepositioning(to) {
            this.$emit('repositioning', to);
            if (to) {
                this.previousImagePosition = { ...this.currentImagePosition };
                if (this.$refs.drag) {
                    const { width, height } = this.$refs.drag.$el.getBoundingClientRect();
                    this.$refs.drag.setPosition(
                        ((this.currentImagePosition.x || 0) * width) / -100,
                        ((this.currentImagePosition.y || 0) * height) / -100,
                        false
                    );
                }
            }
            this.$emit('update:offset', to);
        },
        imageOffset(to) {
            if (this.isRepositioning) return;
            const pos = to || { x: 0, y: 0 };
            this.currentImagePosition = pos;
            this.previousImagePosition = null;
            if (this.$refs.drag) this.$refs.drag.setPosition(0, 0, false);
        }
    },
    mounted() {
        useResponsive(this, this.$el);
        if (this.isContained && this.imageRatio) this.checkRatioSimilarity();
    },
    beforeUnmount() {
        useResponsive.removeElement(this);
    },
    methods: {
        url,
        onImageLoad({ target }) {
            this.image = null;
            this.image = target;
            this.isLoaded = true;
            this.$emit('loaded');
            if (this.isContained || this.cover) this.checkRatioSimilarity();
        },
        showLightbox() {
            if (!this._lightbox) return;
            this.isLightboxShowing = true;
        },
        checkRatioSimilarity() {
            const imageRatio = this.calculatedImageRatio;
            if (!imageRatio) return;
            const containerWidth = this.component.width;
            const containerHeight = this.component.height;
            this.areRatiosSimilar = imageRatio < containerWidth / (containerHeight - (this.imageRatioBreakpoint.x * 2))
                && imageRatio > (containerWidth - (this.imageRatioBreakpoint.y * 2)) / containerHeight;
        },
        setImagePosition(position) {
            if (!this.isRepositioning) return;
            const { width, height } = this.$refs.drag.$el.getBoundingClientRect();
            this.currentImagePosition = {
                x: (position.x / width) * 100,
                y: (position.y / height) * 100
            };
        },
        cancelReposition() {
            let { x, y } = this.previousImagePosition;
            this.$refs.drag.setPosition(x, y * -1);

            this.$emit('reposition', this.previousImagePosition);
            this.isRepositioning = false;
        },
        confirmReposition() {
            this.$emit('reposition', this.currentImagePosition);
            this.isRepositioning = false;
        },
        onImageControl(type) {
            this.$emit('control', type);
            if (type === 'reposition') this.isRepositioning = true;
            if (type === 'confirm:reposition') this.confirmReposition();
            if (type === 'cancel:reposition') this.cancelReposition();
        }
    }
};
</script>

<style lang="less" scoped>
.photo {
    &-wrapper {
        display: inline-block;
        background-color: #FFF;
        &.reposition { position: relative; }
        &.repositioning { z-index: 1; }
        &.fit {
            display: block;
            background-color: ~"rgba(var(--coal-rgb), 0.8)";
        }
        &.editable {
            border: 0;
            .photo-media-controls {
                transition: var(--smooth);
                opacity: 0;
            }
            &:hover {
                border: 0;
                .photo-media-controls { opacity: 1; }
            }
        }
        > img.lightbox { cursor: zoom-in; }
    }
    &-container {
        display: block;
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 100%;
        &.heightless { height: 0; }
    }
    &-background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-size: cover;
        background-position: center;
        opacity: 0.4;
        &.blur {
            filter: blur(16px);
            top: -16px;
            left: -16px;
            right: -16px;
            bottom: -16px;
        }
    }
    &-image {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        max-width: unset;
        &.cover {
            object-fit: cover;
            padding: 0;
            margin: 0;
        }
        &.fade {
            transition: var(--smooth-slow);
            opacity: 0;
            &.loaded { opacity: 1; }
        }
    }
    &-click-handler {
        position: absolute;
        cursor: zoom-in;
    }
}
.photo-wrapper {
    .reposition-hint {
        z-index: 1;
        pointer-events: none;
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        width: 100%;
        justify-content: center;
        .hint {
            display: flex;
            align-items: center;
            background-color: rgba(255, 255, 255, 0.12);
            color: #FFF;
            border-radius: 8px;
            padding: 8px 16px;
        }
    }
    &.backgroundless {
        background-color: transparent;
        &.fit.editable {
            .photo-container {
                border: 2px solid;
                border-color: transparent;
            }
            &:hover .photo-container {
                border-color: ~"rgba(var(--coal-rgb), 0.2)";
                &.primary { border-color: var(--primary); }
                &.translucent { border-color: rgba(255, 255, 255, 0.12); }
                &.translucent-dark { border-color: ~"rgba(var(--coal-rgb), 0.25)"; }
            }
        }
    }
    &.backgroundless, &.reposition {
        .photo-container {
            display: flex;
            align-items: center;
            justify-content: center;
            &.fit-width {
                .drag-wrapper, img { width: 100%; }
            }
            &.fit-height {
                .drag-wrapper, img { height: 100%; }
            }
            .photo-image {
                &.rounded { border-radius: 16px; }
            }
            .photo-image, .drag-wrapper {
                position: static;
                width: auto;
                height: auto;
                object-fit: unset;
            }
        }
    }
}
</style>

<style lang="less">
.photo-wrapper {
    .photo-container {
        &.fit-width .drag-wrapper .drag { width: 100%; }
        &.fit-height .drag-wrapper .drag { height: 100%; }
        .drag-wrapper .drag {
            display: flex;
            align-items: center;
            justify-content: center;
            width: auto;
            height: auto;
        }
    }
}
</style>
